import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Loader2, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

const GPS_OPTIONAL_PREFIXES = ["/doctor", "/delivery", "/messages"];

const GPSGuard = ({ children }) => {
  const location = useLocation();
  const { t } = useTranslation("components");
  const skipGps = GPS_OPTIONAL_PREFIXES.some((p) => location.pathname.startsWith(p));
  const [gpsStatus, setGpsStatus] = useState("checking");
  const [retrying, setRetrying] = useState(false);

  const checkGPS = () => {
    if (!navigator.geolocation) {
      setGpsStatus("unavailable");
      return;
    }

    setRetrying(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        window.__omu_lat = position.coords.latitude;
        window.__omu_lon = position.coords.longitude;
        localStorage.setItem("omu_lat", position.coords.latitude);
        localStorage.setItem("omu_lon", position.coords.longitude);
        setGpsStatus("granted");
        setRetrying(false);
      },
      () => {
        setGpsStatus("denied");
        setRetrying(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  useEffect(() => {
    const savedLat = localStorage.getItem("omu_lat");
    const savedLon = localStorage.getItem("omu_lon");
    if (savedLat && savedLon) {
      window.__omu_lat = parseFloat(savedLat);
      window.__omu_lon = parseFloat(savedLon);
      setGpsStatus("granted");
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          window.__omu_lat = pos.coords.latitude;
          window.__omu_lon = pos.coords.longitude;
          localStorage.setItem("omu_lat", pos.coords.latitude);
          localStorage.setItem("omu_lon", pos.coords.longitude);
        },
        () => {},
        { enableHighAccuracy: false, timeout: 5000 }
      );
      return;
    }
    checkGPS();
  }, []);

  if (skipGps || gpsStatus === "granted") {
    return children;
  }

  if (gpsStatus === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500 mx-auto" />
          <p className="text-slate-600 text-lg">{t("gpsChecking")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center space-y-6">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
          <MapPin className="h-10 w-10 text-red-500" />
        </div>

        <div>
          <h2 className="text-2xl font-bold text-slate-900">{t("gpsRequiredTitle")}</h2>
          <p className="text-slate-600 mt-2">{t("gpsRequiredDesc")}</p>
        </div>

        <div className="bg-slate-50 rounded-xl p-4 text-left space-y-2">
          <p className="font-semibold text-sm text-slate-700">{t("gpsHowToTitle")}</p>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>{t("gpsStep1")}</li>
            <li>{t("gpsStep2")}</li>
            <li>{t("gpsStep3")}</li>
          </ul>
        </div>

        <Button
          className="w-full bg-omu-blue-500 hover:bg-omu-blue-600 h-12 text-base rounded-xl"
          onClick={checkGPS}
          disabled={retrying}
          data-testid="enable-gps-btn"
        >
          {retrying ? (
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-5 w-5 mr-2" />
          )}
          {retrying ? t("gpsVerifying") : t("gpsEnabled")}
        </Button>

        {gpsStatus === "unavailable" && (
          <p className="text-xs text-red-500">{t("gpsUnsupported")}</p>
        )}
      </div>
    </div>
  );
};

export default GPSGuard;
